import { balance, type Balance } from './balance';
import { attackOf, effectiveStat, hpBasis, hpLossForLoss, resolveMatchup } from './matchup';
import { Motion } from './motion';
import { DEFENSE_SLOTS, OFFENSE_SLOTS, SLOT_POSITION } from './playdef';
import type { Rng } from './rng';
import {
  carry,
  gapBlockOf,
  intercept,
  keysAlong,
  pursuitOf,
  runTrack,
  secondLevel,
  speedOf,
  type Block,
  type CarryContext,
  type CarryTrack,
  type Pursuer,
} from './run';
import type {
  Combatant,
  DefenseAssignment,
  DefenseSlot,
  GameState,
  HpCause,
  Move,
  OffenseSlot,
  PlayCall,
  PlayEvent,
  PlayResult,
  Position,
  Spot,
  UnitId,
} from './types';

/**
 * Play resolution: one snap from a GameState and both play calls to the next state and an
 * ordered event log. Order of resolution (and of rng draws) is fixed. A pass: move spends, blocks,
 * coverage, a screen's second-level blocks, the throw, the contested catch, the tackle chain (a
 * screen's catch runs the run game's chain instead). Coverage resolves before the throw because
 * the QB's read depends on it; the log is still sorted by time. A run (the QB hands off): move
 * spends, blocks, second-level blocks, the run's tackle chain (run.ts).
 *
 * A spent attack powers its Pokémon's first battle in that order. HP changes (every lost battle,
 * recoil, drain, self-KO) are worked out from the final log, so a battle the whistle cut off costs
 * nothing, and they land in the next state: battles within a snap use snap-time HP.
 *
 * Positions: the play JSON's formation and routes become `move` legs for every Pokémon (see
 * motion.ts), timed by the same resolution, so the client only interpolates.
 *
 * Pure: the input state is never mutated. All numbers come from balance.json.
 */

export interface PlayOutcome {
  state: GameState;
  events: PlayEvent[];
  /** Net yards from the line of scrimmage to the end spot. */
  yards: number;
  result: PlayResult;
  firstDown: boolean;
  /** The offense ran out of downs on this play. Possession changes in slice 6. */
  turnoverOnDowns: boolean;
  /** The carrier went down out of bounds: the clock stops. */
  outOfBounds: boolean;
}

type Depth = 'short' | 'medium' | 'deep';
interface Route {
  depthYards: number;
  breakSeconds: number;
  depth: Depth;
  /** A screen: the catch becomes a run behind lead blocks. */
  carry?: boolean;
}

/** Who covers whom when the defense has no man assignment saying otherwise. */
const DEFAULT_COVER: Partial<Record<OffenseSlot, DefenseSlot>> = {
  WR1: 'CB1',
  WR2: 'CB2',
  TE: 'S',
  RB: 'LB1',
};

/** Blockers pick up rushers in this order: the line first, then the tight end, then the back. */
const BLOCK_ORDER: readonly OffenseSlot[] = ['OL1', 'OL2', 'TE', 'RB', 'WR1', 'WR2'];

const off = (s: OffenseSlot): UnitId => `off:${s}`;
const def = (s: DefenseSlot): UnitId => `def:${s}`;

const clamp = (x: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, x));

/** Interpolate a time between its value at margin 1 and at margin 3 (balance.json names). */
function byMargin(margin: number, at1: number, at3: number): number {
  return at1 + (at3 - at1) * clamp((margin - 1) / (3 - 1), 0, 1);
}

/**
 * An open receiver's separation from his coverage battle's margin (balance.json `separation`):
 * "steps", whole steps, one more each time the margin multiplies by marginPerStep; "continuous"
 * (slice 10g), (margin - 1) / marginPerStep unrounded, so a small edge is a small lead. Both cap
 * at maxSteps.
 */
export function separationSteps(margin: number, b: Balance): number {
  const s = b.separation;
  const steps =
    s.mode === 'continuous'
      ? (margin - 1) / s.marginPerStep
      : 1 + Math.floor(Math.log(margin) / Math.log(s.marginPerStep));
  return Math.min(s.maxSteps, steps);
}

const sub = (a: Spot, c: Spot): Spot => [a[0] - c[0], a[1] - c[1]];
const addTo = (a: Spot, c: Spot): Spot => [a[0] + c[0], a[1] + c[1]];
const scale = (a: Spot, k: number): Spot => [a[0] * k, a[1] * k];
function unit(a: Spot): Spot {
  const d = Math.hypot(a[0], a[1]);
  return d === 0 ? [1, 0] : [a[0] / d, a[1] / d];
}

function routeOf(name: string | undefined, b: Balance): Route {
  const routes = b.routes as Record<
    string,
    { depthYards: number; breakSeconds: number; depth: string; carry?: boolean }
  >;
  const r = name === undefined ? undefined : routes[name];
  if (!r) throw new Error(`unknown route "${name}"; add it to balance.json routes`);
  if (r.depth !== 'short' && r.depth !== 'medium' && r.depth !== 'deep') {
    throw new Error(`route "${name}" has unknown depth "${r.depth}"`);
  }
  return r as Route;
}

/**
 * A coverage defender's multiplier against a route: its zone landmark's (balance.json
 * coverage.zones, medium and deep routes only, times the play-action fake's) or press man's
 * (coverage.man, every depth). A zone never changes a short route.
 */
function coverageMod(a: DefenseAssignment, route: Route, playAction: boolean, b: Balance): number {
  const C = b.coverage;
  if (a.role === 'man') return C.man[route.depth];
  if (a.role !== 'zone' || route.depth === 'short' || a.zone === undefined) return 1;
  const zones = C.zones as Record<string, { medium: number; deep: number } | undefined>;
  const z = zones[a.zone];
  if (!z) throw new Error(`unknown zone "${a.zone}"; add it to balance.json coverage.zones`);
  return z[route.depth] * (playAction ? b.playAction.zoneMod : 1);
}

/** Fastest first; ties keep slot order (Array.prototype.sort is stable). */
function bySpeed<S extends string>(slots: S[], lineup: Record<S, Combatant>, b: Balance): S[] {
  return [...slots].sort(
    (x, y) => effectiveStat(lineup[y], 'spe', b) - effectiveStat(lineup[x], 'spe', b),
  );
}

/** What one side spent at the snap that lasts only this play. */
interface Spent<S extends string> {
  bursts: Map<S, number>;
  /** Attacks not yet used; the Pokémon's first battle takes its attack out. */
  attacks: Map<S, Move>;
}

/**
 * Spend the moves chosen for one side: decisions override the play's default script. Mutates the
 * working copy `lineup` (PP, persistent stages) and returns this-play-only bursts and attacks.
 */
function spendMoves<S extends string>(
  lineup: Record<S, Combatant>,
  slots: readonly S[],
  defaults: Record<S, string | null>,
  decisions: Partial<Record<S, string | null>> | undefined,
  unit: (s: S) => UnitId,
  events: PlayEvent[],
  b: Balance,
): Spent<S> {
  const bursts = new Map<S, number>();
  const attacks = new Map<S, Move>();
  for (const s of slots) {
    const decided = decisions !== undefined && s in decisions;
    const choice = decided ? decisions[s] : defaults[s];
    if (!choice) continue;
    const c = lineup[s];
    // A fresh sub spends nothing on its first snap, whatever was chosen for its slot.
    if (b.subs.noMoveOnFirstSnap && c.enteredThisPlay) continue;
    const move = c.moves.find((m) => m.id === choice);
    if (!move) {
      // A coach's decision must name a move the Pokémon knows; a play's default script may not fit
      // whoever is in the slot, and is skipped.
      if (decided) throw new Error(`${unit(s)} does not know "${choice}"`);
      continue;
    }
    if (c.hp <= 0) continue;
    const left = c.ppLeft[move.id] ?? 0;
    if (left <= 0) continue;
    const effect = move.effect;
    // Protect, status, heal, and debuff effects land with slices 6 and 9.
    if (effect.kind !== 'burst' && effect.kind !== 'stage' && effect.kind !== 'attack') continue;
    c.ppLeft = { ...c.ppLeft, [move.id]: left - 1 };
    if (effect.kind === 'attack') {
      attacks.set(s, move);
    } else if (effect.kind === 'burst') {
      bursts.set(s, (bursts.get(s) ?? 0) + effect.speedStages);
    } else {
      c.stages = {
        ...c.stages,
        [effect.stat]: clamp(
          (c.stages[effect.stat] ?? 0) + effect.stages,
          -b.stages.cap,
          b.stages.cap,
        ),
      };
    }
    events.push({ t: 0, type: 'spend', unit: unit(s), moveId: move.id, ppLeft: left - 1 });
  }
  return { bursts, attacks };
}

/** The combatants as they play this snap, as copies: persistent state plus this-play bursts. */
function inPlay<S extends string>(
  lineup: Record<S, Combatant>,
  bursts: Map<S, number>,
  b: Balance,
): Record<S, Combatant> {
  const out = Object.fromEntries(
    Object.entries<Combatant>(lineup).map(([s, c]) => [s, { ...c }]),
  ) as Record<S, Combatant>;
  for (const [s, n] of bursts) {
    const c = lineup[s];
    out[s] = {
      ...c,
      stages: { ...c.stages, spe: Math.min(b.stages.cap, (c.stages.spe ?? 0) + n) },
    };
  }
  return out;
}

/**
 * A copy of the state play resolution may write to. Resolution only ever replaces a combatant's
 * own fields (hp, ppLeft, stages, enteredThisPlay), never edits its moves or stats in place, so a
 * shallow copy of each combatant is enough; structuredClone was half the cost of a snap.
 */
function cloneState(state: GameState): GameState {
  const copy = <S extends string>(lineup: Record<S, Combatant>) => {
    const out = {} as Record<S, Combatant>;
    for (const s of Object.keys(lineup) as S[]) out[s] = { ...lineup[s] };
    return out;
  };
  return {
    ...state,
    offense: { lineup: copy(state.offense.lineup) },
    defense: { lineup: copy(state.defense.lineup) },
  };
}

/** A play-JSON spot (relative to the ball) on the field, the ball on `los`. */
const fieldSpot =
  (los: number, b: Balance) =>
  (rel: Spot, lateral = rel[1]): Spot => [
    clamp(los + rel[0], -b.field.endZoneYards, 100 + b.field.endZoneYards),
    lateral,
  ];

interface Coverage {
  routeSlots: OffenseSlot[];
  /** Each route runner and the defender who covers him. */
  cover: Map<OffenseSlot, DefenseSlot>;
  /** Coverage defenders nobody was paired with, fastest first. */
  free: DefenseSlot[];
}

/** Coverage pairs. Man targets first, then the position pairing, then the fastest spare defender. */
function coverageOf(
  oa: PlayCall['offense']['assignments'],
  da: PlayCall['defense']['assignments'],
  D: Record<DefenseSlot, Combatant>,
  b: Balance,
): Coverage {
  const routeSlots = OFFENSE_SLOTS.filter((s) => oa[s].role === 'route');
  const coverers = DEFENSE_SLOTS.filter((s) => da[s].role === 'zone' || da[s].role === 'man');
  const cover = new Map<OffenseSlot, DefenseSlot>();
  const claimed = new Set<DefenseSlot>();
  for (const d of coverers) {
    const target = da[d].target;
    if (target !== undefined && routeSlots.includes(target) && !cover.has(target)) {
      cover.set(target, d);
      claimed.add(d);
    }
  }
  const spare = () => coverers.filter((d) => !claimed.has(d) && da[d].target === undefined);
  for (const s of routeSlots) {
    const d = DEFAULT_COVER[s];
    if (cover.has(s) || d === undefined || !spare().includes(d)) continue;
    cover.set(s, d);
    claimed.add(d);
  }
  for (const s of routeSlots) {
    if (cover.has(s)) continue;
    const d = bySpeed(spare(), D, b)[0];
    if (d === undefined) break;
    cover.set(s, d);
    claimed.add(d);
  }
  const free = bySpeed(
    coverers.filter((d) => !claimed.has(d)),
    D,
    b,
  );
  return { routeSlots, cover, free };
}

/**
 * Where all fourteen line up. A coverage defender paired with a receiver lines up over him at his
 * own depth; a defense play cannot know the offense's formation.
 */
function formationOf(
  call: Pick<PlayCall, 'offense' | 'defense'>,
  cover: ReadonlyMap<OffenseSlot, DefenseSlot>,
  spot: (rel: Spot, lateral?: number) => Spot,
): { formation: Record<UnitId, Spot>; qb: OffenseSlot } {
  const oa = call.offense.assignments;
  const da = call.defense.assignments;
  const qb = OFFENSE_SLOTS.find((s) => oa[s].role === 'pass' || oa[s].role === 'hand-off');
  if (qb === undefined) throw new Error(`${call.offense.id}: no passer and no hand-off`);
  const coveredBy = new Map([...cover].map(([o, d]) => [d, o] as const));
  const formation = {} as Record<UnitId, Spot>;
  for (const s of OFFENSE_SLOTS) formation[off(s)] = spot(oa[s].align);
  for (const d of DEFENSE_SLOTS) {
    const o = coveredBy.get(d);
    formation[def(d)] = spot(da[d].align, o === undefined ? undefined : oa[o].align[1]);
  }
  return { formation, qb };
}

/**
 * The formation two plays line up in before the snap, exactly as `resolvePlay` places them, with
 * nothing spent yet (a burst can change which spare defender is fastest, so after spends the
 * snap's own formation may pair one defender differently). Presentation: the field shows it
 * between plays.
 */
export function snapFormation(
  state: GameState,
  call: Pick<PlayCall, 'offense' | 'defense'>,
  b: Balance = balance,
): { formation: Record<UnitId, Spot>; ball: UnitId } {
  const D = inPlay(state.defense.lineup, new Map(), b);
  const { cover } = coverageOf(call.offense.assignments, call.defense.assignments, D, b);
  const { formation, qb } = formationOf(call, cover, fieldSpot(state.ballOn, b));
  return { formation, ball: off(qb) };
}

export function resolvePlay(
  state: GameState,
  call: PlayCall,
  rng: Rng,
  b: Balance = balance,
): PlayOutcome {
  const next = cloneState(state);
  const events: PlayEvent[] = [];
  const oa = call.offense.assignments;
  const da = call.defense.assignments;
  const F = b.field;
  const los = state.ballOn;
  const toGoal = 100 - los;
  const at = (yards: number, lateral = 0): Spot => [clamp(los + yards, 0, 100), lateral];
  /** A play-JSON spot (relative to the ball) on the field. */
  const spot = fieldSpot(los, b);

  // 1. Move spends, offense then defense.
  const oSpent = spendMoves(
    next.offense.lineup,
    OFFENSE_SLOTS,
    call.offense.defaultMoves,
    call.offenseMoves,
    off,
    events,
    b,
  );
  const dSpent = spendMoves(
    next.defense.lineup,
    DEFENSE_SLOTS,
    call.defense.defaultMoves,
    call.defenseMoves,
    def,
    events,
    b,
  );
  const O = inPlay(next.offense.lineup, oSpent.bursts, b);
  const D = inPlay(next.defense.lineup, dSpent.bursts, b);
  /**
   * One battle; each side's unused attack goes into it, and a miss is logged before it. `bWith` is
   * a second defender averaged into the b side (slice 10q); his own attack, if any, stays his.
   */
  const battle = (
    kind: 'block' | 'coverage' | 'contested' | 'tackle' | 'throw',
    o: OffenseSlot,
    d: DefenseSlot,
    t: number,
    bMod = 1,
    bWith?: DefenseSlot,
  ) => {
    const aAttack = oSpent.attacks.get(o);
    const bAttack = dSpent.attacks.get(d);
    oSpent.attacks.delete(o);
    dSpent.attacks.delete(d);
    const r = resolveMatchup(
      {
        kind,
        a: O[o],
        b: D[d],
        ...(bMod !== 1 ? { bMod } : {}),
        ...(bWith !== undefined ? { bWith: D[bWith] } : {}),
        ...(aAttack ? { aAttack } : {}),
        ...(bAttack ? { bAttack } : {}),
      },
      rng,
      b,
    );
    if (r.aAttack?.hit === false) {
      events.push({ t, type: 'miss', unit: off(o), moveId: r.aAttack.moveId });
    }
    if (r.bAttack?.hit === false) {
      events.push({ t, type: 'miss', unit: def(d), moveId: r.bAttack.moveId });
    }
    return r;
  };

  // 2. Coverage pairs. Man targets first, then the position pairing, then the fastest spare defender.
  const { routeSlots, cover, free } = coverageOf(oa, da, D, b);

  // The formation, and the motion it starts.
  const { formation, qb } = formationOf(call, cover, spot);
  const run = oa[qb].role === 'hand-off';
  events.unshift({ t: 0, type: 'snap', formation, ball: off(qb) });
  const m = new Motion(formation);
  const dropback = oa[qb].path;
  if (dropback)
    m.run(
      off(qb),
      dropback.map((p) => spot(p)),
      0,
      F.dropbackSeconds,
    );
  // Zone defenders drop at the snap on a pass; on a run they read first, then chase (run.ts).
  for (const d of run ? [] : free) {
    const drop = da[d].drop;
    if (drop) m.run(def(d), [spot(drop)], 0, F.zoneDropSeconds);
  }
  /** A rusher who sheds at t0 gets home at t1, just in front of the passer. */
  const rushHome = (ru: DefenseSlot, t0: number, t1: number) =>
    m.goto(def(ru), addTo(m.posAt(off(qb), t1), [F.tackleGapYards, 0]), t0, t1);

  // A screen: the first read runs a carry route. The line holds, then lets its rushers go and leads.
  const firstRead = call.offense.reads[0] ?? routeSlots[0];
  const screenPlay =
    !run && firstRead !== undefined && routeOf(oa[firstRead].route, b).carry === true;
  const S = b.screen;

  // 3. Blocks. Each rusher gets the next blocker in BLOCK_ORDER; extras come in unblocked. On a
  // run, a blocker who cannot meet his rusher on the rusher's way to the designed gap before the
  // rusher gets there does not block him: the rusher comes in unblocked, the blocker climbs.
  const blockers = BLOCK_ORDER.filter((s) => oa[s].role === 'block');
  // A blitzer is a rusher who lines up off the line.
  const rushers = DEFENSE_SLOTS.filter((s) => da[s].role === 'rush' || da[s].role === 'blitz');
  const runCarrier = run ? OFFENSE_SLOTS.find((s) => oa[s].role === 'carry') : undefined;
  const designedGap =
    runCarrier === undefined ? undefined : spot((oa[runCarrier].path ?? []).at(-1) as Spot);
  const lineBlocker = new Map<DefenseSlot, OffenseSlot | undefined>(
    rushers.map((ru, i) => {
      const bl = blockers[i];
      if (bl === undefined || designedGap === undefined) return [ru, bl];
      const from = formation[def(ru)];
      const way: CarryTrack = [
        { t: 0, at: from },
        {
          t: Math.hypot(designedGap[0] - from[0], designedGap[1] - from[1]) / speedOf(D[ru], b),
          at: designedGap,
        },
      ];
      const meets = intercept(way, 0, formation[off(bl)], 0, speedOf(O[bl], b), F.engageGapYards);
      return [ru, meets === undefined ? undefined : bl];
    }),
  );
  /** When each rusher gets home; `crit`: he won his block on a crit, which is always a sack. */
  const arrivals: { slot: DefenseSlot; t: number; crit: boolean }[] = [];
  /** On a screen: rushers the line let go, and the line blockers who lead. */
  const letGo: DefenseSlot[] = [];
  const leads: OffenseSlot[] = [];
  /** When each blocked or unblocked defender gets free (Infinity: pancaked), for the run game. */
  const release = new Map<DefenseSlot, number>();
  const lineBlocks: Block[] = [];
  const qbSpot = dropback ? spot(dropback.at(-1) as Spot) : formation[off(qb)];
  rushers.forEach((ru) => {
    const bl = lineBlocker.get(ru);
    if (bl === undefined) {
      // Unblocked: a first step, then from where he lined up to the passer at his own speed.
      const [x, y] = formation[def(ru)];
      const home = Math.max(
        b.timing.rusherToQbSeconds,
        b.timing.unblockedFirstStepSeconds +
          Math.hypot(qbSpot[0] - x, qbSpot[1] - y) / speedOf(D[ru], b),
      );
      events.push({ t: 0, type: 'shed', unit: def(ru), blocker: null });
      arrivals.push({ slot: ru, t: home, crit: false });
      release.set(ru, 0);
      if (!run) rushHome(ru, 0, home);
      return;
    }
    // Blocker and rusher meet halfway, a step apart.
    const [bx, by] = formation[off(bl)];
    const [rx, ry] = formation[def(ru)];
    const mid: Spot = [(bx + rx) / 2, (by + ry) / 2];
    m.goto(off(bl), [mid[0] - F.engageGapYards / 2, mid[1]], 0, F.engageSeconds);
    m.goto(def(ru), [mid[0] + F.engageGapYards / 2, mid[1]], 0, F.engageSeconds);
    const r = battle('block', bl, ru, 0);
    events.push({ t: 0, type: 'battle', a: off(bl), b: def(ru), matchup: r, at: mid });
    if (screenPlay && r.crit !== 'b') {
      // A screen: held until screen.holdSeconds whatever the margin, then let go; the blocker leads.
      const pancake = r.crit === 'a';
      events.push({
        t: 0,
        type: 'hold',
        unit: off(bl),
        rusher: def(ru),
        until: S.holdSeconds,
        pancake,
      });
      leads.push(bl);
      if (pancake) release.set(ru, Infinity);
      else {
        events.push({
          t: S.holdSeconds,
          type: 'shed',
          unit: def(ru),
          blocker: off(bl),
          screen: true,
        });
        letGo.push(ru);
      }
      return;
    }
    if (r.winner === 'b') {
      const t =
        r.crit === 'b'
          ? 0
          : byMargin(r.margin, b.timing.shedSecondsAtMargin1, b.timing.shedSecondsAtMargin3);
      events.push({ t, type: 'shed', unit: def(ru), blocker: off(bl) });
      arrivals.push({ slot: ru, t: t + b.timing.rusherToQbSeconds, crit: r.crit === 'b' });
      release.set(ru, t);
      // On a run, winning his block (not on a crit) puts him in his gap until he sheds, driving
      // the blocker back by the margin (run.pushYards).
      let standsAt: Spot | undefined;
      if (run && r.crit !== 'b') {
        const R = b.run;
        const push = byMargin(r.margin, R.pushYardsAtMargin1, R.pushYardsAtMargin3);
        standsAt = [mid[0] + F.engageGapYards / 2 - push, mid[1]];
        const until = Math.max(F.engageSeconds, Math.min(R.handoffSeconds, t));
        m.goto(def(ru), standsAt, F.engageSeconds, until);
        m.goto(off(bl), [standsAt[0] - F.engageGapYards, mid[1]], F.engageSeconds, until);
      }
      lineBlocks.push({
        blocker: bl,
        defender: ru,
        lateral: mid[1],
        release: t,
        ...(standsAt ? { standsAt } : {}),
      });
      if (!run) rushHome(ru, t, t + b.timing.rusherToQbSeconds);
    } else {
      const pancake = r.crit === 'a';
      const until = byMargin(
        r.margin,
        b.timing.holdSecondsAtMargin1,
        b.timing.holdSecondsAtMargin3,
      );
      events.push({ t: 0, type: 'hold', unit: off(bl), rusher: def(ru), until, pancake });
      const freeAt = pancake ? Infinity : until;
      release.set(ru, freeAt);
      lineBlocks.push({ blocker: bl, defender: ru, lateral: mid[1], release: freeAt });
      if (!pancake) {
        events.push({ t: until, type: 'shed', unit: def(ru), blocker: off(bl) });
        arrivals.push({ slot: ru, t: until + b.timing.rusherToQbSeconds, crit: false });
        if (!run) rushHome(ru, until, until + b.timing.rusherToQbSeconds);
      }
    }
  });
  arrivals.sort((x, y) => x.t - y.t);

  // Play-action is max protect: the backs and tight end stay in, and each spare blocker picks up
  // the next rusher to get free, in one more block battle where he meets him in front of the passer.
  if (!run && call.offense.playAction) {
    const picked = arrivals.splice(0, blockers.length - rushers.length);
    for (const [i, a] of picked.entries()) {
      const bl = blockers[rushers.length + i] as OffenseSlot;
      const ru = a.slot;
      const f = release.get(ru) ?? 0;
      const meet = addTo(m.posAt(off(qb), f), [F.engageGapYards * 2, 0]);
      m.goto(off(bl), [meet[0] - F.engageGapYards / 2, meet[1]], 0, Math.max(f, F.engageSeconds));
      m.goto(def(ru), [meet[0] + F.engageGapYards / 2, meet[1]], f, f + F.engageSeconds);
      const r = battle('block', bl, ru, f);
      events.push({ t: f, type: 'battle', a: off(bl), b: def(ru), matchup: r, at: meet });
      let freeAt: number;
      const crit = r.winner === 'b' && r.crit === 'b';
      if (r.winner === 'b') {
        freeAt =
          f +
          (r.crit === 'b'
            ? 0
            : byMargin(r.margin, b.timing.shedSecondsAtMargin1, b.timing.shedSecondsAtMargin3));
        events.push({ t: freeAt, type: 'shed', unit: def(ru), blocker: off(bl) });
      } else {
        const pancake = r.crit === 'a';
        const until =
          f + byMargin(r.margin, b.timing.holdSecondsAtMargin1, b.timing.holdSecondsAtMargin3);
        events.push({ t: f, type: 'hold', unit: off(bl), rusher: def(ru), until, pancake });
        freeAt = pancake ? Infinity : until;
        if (!pancake) events.push({ t: until, type: 'shed', unit: def(ru), blocker: off(bl) });
      }
      release.set(ru, freeAt);
      if (freeAt !== Infinity) {
        const home = Math.max(freeAt, f + F.engageSeconds);
        arrivals.push({ slot: ru, t: home + b.timing.rusherToQbSeconds, crit });
        rushHome(ru, home, home + b.timing.rusherToQbSeconds);
      }
    }
    arrivals.sort((x, y) => x.t - y.t);
  }

  const ctx: CarryContext = {
    b,
    rng,
    m,
    O,
    D,
    events,
    los,
    battle: (kind, o, d, t, bMod, bWith) => battle(kind, o, d, t, bMod, bWith),
    byMargin,
  };
  const lineUsed = new Set(lineBlocker.values());
  const spareBlockers = blockers.filter((s) => !lineUsed.has(s));
  const offTheLine = DEFENSE_SLOTS.filter((d) => !rushers.includes(d));
  /** On a screen, spare blockers climb to whoever is not rushing, the back's cover man first. */
  const climb = (first?: DefenseSlot) =>
    secondLevel(ctx, spareBlockers, offTheLine, formation, first, release);
  /** Everyone not held out of the play, chasing from `react(slot)` at the earliest (a rusher from `rusherReact`). */
  const pursuers = (react: (d: DefenseSlot) => number, rusherReact: number): Pursuer[] =>
    DEFENSE_SLOTS.flatMap((d) => {
      const t = rushers.includes(d) ? rusherReact : react(d);
      const r = release.get(d) ?? 0;
      if (r === Infinity) return [];
      return [{ slot: d, t0: Math.max(r, t) }];
    });

  let yards = 0;
  let result: PlayResult | undefined;
  let endT: number;
  let outOfBounds = false;

  if (run) {
    // The run game (run.ts): the handoff and the carrier's read of the line, the defenders' reads,
    // second-level blocks on their way to him, the chase.
    const carrier = OFFENSE_SLOTS.find((s) => oa[s].role === 'carry') as OffenseSlot;
    const path = (oa[carrier].path ?? []).map((p) => spot(p));
    const gap = path.at(-1) as Spot;
    const R = b.run;
    const { track, blocker, cutback } = runTrack(
      ctx,
      carrier,
      formation[off(carrier)],
      path,
      lineBlocks,
    );
    // Each defender off the line reads the run in his own time (one roll each, in slot order) and
    // goes for the carrier; a spare blocker meets him on the way, or he is free.
    const reads = new Map(
      offTheLine.map((d) => [d, rng.roll(R.readSeconds.min, R.readSeconds.max)] as const),
    );
    const readOf = (d: DefenseSlot) => reads.get(d) as number;
    const ways = new Map(
      offTheLine.map((d) => {
        const p = pursuitOf(
          track,
          R.handoffSeconds,
          formation[def(d)],
          readOf(d),
          speedOf(D[d], b),
          R.tackleReachYards,
        );
        return [d, p.path] as const;
      }),
    );
    const targets: Partial<Record<OffenseSlot, DefenseSlot>> = {};
    for (const s of spareBlockers) {
      const t = oa[s].target;
      if (t !== undefined) targets[s] = t;
    }
    secondLevel(
      ctx,
      spareBlockers,
      offTheLine,
      formation,
      undefined,
      release,
      {
        start: 0,
        earliest: R.climbSeconds,
        blockerAt: (bl) => formation[off(bl)],
        defenderAt: (d) => formation[def(d)],
        pursuit: (d) => ways.get(d) as CarryTrack,
      },
      { gap, edge: gapBlockOf(lineBlocks, gap, b) === undefined, targets },
    );
    // A defender winning his line block stands in his gap until he sheds: a carrier who comes by
    // him there meets him.
    const inGap: Pursuer[] = lineBlocks.flatMap((x) =>
      x.standsAt === undefined
        ? []
        : [
            {
              slot: x.defender,
              t0: Math.max(F.engageSeconds, Math.min(R.handoffSeconds, x.release)),
              at: x.standsAt,
              until: x.release,
            },
          ],
    );
    const out = carry(ctx, carrier, track, R.handoffSeconds, [...inGap, ...pursuers(readOf, 0)]);
    ({ yards, result, endT, outOfBounds } = out);
    const h = R.handoffSeconds;
    // Where the motion has him at the handoff (a tackle right after it cuts his last leg short).
    const handAt = m.posAt(off(carrier), h);
    m.goto(off(qb), [handAt[0], handAt[1] + (handAt[1] >= 0 ? -1 : 1)], 0, Math.min(h, endT));
    events.push({ t: h, type: 'handoff', from: off(qb), to: off(carrier), at: handAt });
    const hole = out.track.find((k) => k.hole);
    if (hole && hole.t <= endT) {
      events.push({
        t: hole.t,
        type: 'hole',
        unit: off(carrier),
        at: hole.at,
        behind: blocker === null ? null : off(blocker),
        cutback,
      });
    }
  } else {
    // 4. Coverage battles, at each route's break.
    interface Receiver {
      slot: OffenseSlot;
      route: Route;
      by: DefenseSlot | undefined;
      open: boolean;
      steps: number;
      /** His coverage battle's margin (Infinity with no battle): separation finer than steps. */
      margin: number;
      pick: boolean;
      /** Where the route breaks; a ball thrown to him comes down here. */
      brk: Spot;
    }
    const receivers = new Map<OffenseSlot, Receiver>();
    for (const s of routeSlots) {
      const route = routeOf(oa[s].route, b);
      const rel = oa[s].path ?? [];
      if (rel.at(-1)?.[0] !== route.depthYards) {
        throw new Error(
          `${call.offense.id}.${s}: a ${oa[s].route} path must end ${route.depthYards} yards downfield`,
        );
      }
      // A route never runs past the goal line: the catch is there at the latest.
      const path = rel.map((p): Spot => {
        const [x, y] = spot(p);
        return [Math.min(x, 100), y];
      });
      const brk = path.at(-1) as Spot;
      m.run(off(s), path, 0, route.breakSeconds);
      const by = cover.get(s);
      const rec: Receiver = {
        slot: s,
        route,
        by,
        open: true,
        steps: b.separation.maxSteps,
        margin: Infinity,
        pick: false,
        brk,
      };
      if (by !== undefined && route.carry) {
        // A screen is thrown behind the line: no coverage battle. His cover man waits upfield of
        // the catch, his steps away, between him and the goal line (slice 6e).
        rec.steps = S.openSteps;
        m.goto(def(by), addTo(brk, [rec.steps * F.coverYardsPerStep, 0]), 0, route.breakSeconds);
      } else if (by !== undefined) {
        const r = battle(
          'coverage',
          s,
          by,
          route.breakSeconds,
          coverageMod(da[by], route, call.offense.playAction === true, b),
        );
        events.push({
          t: route.breakSeconds,
          type: 'battle',
          a: off(s),
          b: def(by),
          matchup: r,
          at: brk,
        });
        rec.open = r.winner === 'a';
        rec.margin = r.margin;
        rec.pick = r.crit === 'b';
        rec.steps = !rec.open
          ? 0
          : r.crit === 'a'
            ? b.crits.coverageOpenSteps
            : separationSteps(r.margin, b);
        // Covered: sitting on the break. Open: trailing by his steps along the route.
        const prev = path.at(-2) ?? formation[off(s)];
        m.goto(
          def(by),
          rec.open
            ? addTo(brk, scale(unit(sub(brk, prev)), -rec.steps * F.coverYardsPerStep))
            : addTo(brk, [F.coverTightYards, 0]),
          0,
          route.breakSeconds,
        );
      }
      events.push(
        rec.open
          ? { t: route.breakSeconds, type: 'open', unit: off(s), steps: rec.steps }
          : { t: route.breakSeconds, type: 'covered', unit: off(s), by: def(by as DefenseSlot) },
      );
      receivers.set(s, rec);
    }

    // A screen's spare blockers release to the second level once coverage has paired up.
    const screen = [...receivers.values()].find((r) => r.route.carry);
    if (screen) climb(screen.by);
    // 5. The throw: first open read, else the first read once every read has broken. A play-action
    // fake costs its time first. `throws.readChoice` "bestOpen" (slice 11c, the default since 6f):
    // among the reads open at the throw, the most separation (steps, then the coverage battle's
    // margin), ties in progression order; "first" takes the first open read. The throw's timing is
    // the same either way.
    const reads = (call.offense.reads.length > 0 ? call.offense.reads : routeSlots)
      .map((s) => receivers.get(s))
      .filter((r): r is Receiver => r !== undefined);
    const first = reads[0];
    if (first === undefined) throw new Error(`${call.offense.id}: a pass play needs a route`);
    const bestOpen = b.throws.readChoice === 'bestOpen';
    /** The open read with the most steps, then margin; the earliest in the progression on a tie. */
    const mostOpen = (rs: readonly Receiver[]) =>
      rs.reduce<Receiver | undefined>(
        (best, r) =>
          !best || r.steps > best.steps || (r.steps === best.steps && r.margin > best.margin)
            ? r
            : best,
        undefined,
      );
    const firstOpen = reads.find((r) => r.open);
    let target = firstOpen ?? first;
    const readT =
      (target.open
        ? target.route.breakSeconds
        : Math.max(...reads.map((r) => r.route.breakSeconds))) +
      (call.offense.playAction ? b.playAction.fakeSeconds : 0);
    let throwT = readT + b.timing.releaseSeconds;
    if (bestOpen && firstOpen) {
      target = mostOpen(reads.filter((r) => r.open && r.route.breakSeconds <= throwT)) ?? firstOpen;
    }
    const firstRusher = arrivals[0];
    let pressured = firstRusher !== undefined && firstRusher.t < throwT;
    let hot = false;
    let branch: 'throw' | 'sack' | 'throw-away' = 'throw';

    // Pressure: the first rusher home before the read. A crit is a sack; else a hot throw to the
    // first read that has broken open, else to the first that has broken; else throw it away once
    // throws.minThrowSeconds have passed; else a sack.
    if (firstRusher !== undefined && firstRusher.t <= readT) {
      const tP = firstRusher.t;
      const broken = reads.filter((r) => r.route.breakSeconds <= tP);
      const openNow = bestOpen
        ? mostOpen(broken.filter((r) => r.open))
        : broken.find((r) => r.open);
      const hotRead = firstRusher.crit ? undefined : (openNow ?? broken[0]);
      if (hotRead !== undefined) {
        target = hotRead;
        throwT = tP;
        pressured = true;
        hot = true;
      } else if (!firstRusher.crit && tP >= b.throws.minThrowSeconds) branch = 'throw-away';
      else branch = 'sack';
    }

    // Rushers the line let go on a screen reach the passer after the throw, then recover.
    for (const ru of letGo) {
      const home = Math.max(S.holdSeconds + b.timing.rusherToQbSeconds, throwT);
      rushHome(ru, S.holdSeconds, home);
      release.set(ru, home + S.recoverSeconds);
    }
    // At screen.holdSeconds the line leads: each takes the nearest pursuer nobody has blocked, if
    // he gets to him before the ball comes down; one he cannot reach in time stays free.
    if (screenPlay && branch === 'throw' && leads.length > 0) {
      const h = S.holdSeconds;
      secondLevel(
        ctx,
        leads,
        DEFENSE_SLOTS.filter((d) => !rushers.includes(d) && !release.has(d)),
        formation,
        undefined,
        release,
        {
          start: h,
          earliest: h,
          latest: () => throwT + b.timing.flightSeconds[target.route.depth],
          blockerAt: (bl) => m.posAt(off(bl), h),
          defenderAt: (d) => m.posAt(def(d), h),
        },
      );
    }

    if (branch === 'throw-away' && firstRusher !== undefined) {
      const tP = firstRusher.t;
      const from = m.posAt(off(qb), tP);
      const side = from[1] >= 0 ? 1 : -1;
      const arrives = tP + b.timing.flightSeconds.short;
      events.push({
        t: tP,
        type: 'throw-away',
        unit: off(qb),
        by: def(firstRusher.slot),
        at: [Math.min(from[0] + F.missYards, 100), side * (F.widthYards / 2 + 1)],
        arrives,
      });
      endT = arrives;
      result = 'incomplete';
    } else if (branch === 'sack' && firstRusher !== undefined) {
      // Sacked before the read.
      yards = -Math.min(b.throws.sackYards, los);
      endT = firstRusher.t;
      events.push({
        t: endT,
        type: 'sack',
        unit: off(qb),
        by: def(firstRusher.slot),
        yards,
        at: m.posAt(off(qb), endT),
      });
      result = los + yards <= 0 ? 'safety' : 'sack';
    } else {
      const catchT = throwT + b.timing.flightSeconds[target.route.depth];
      const depth = Math.min(target.route.depthYards, toGoal);
      const lat = target.brk[1];
      // A catch behind the line (a screen) is where the route broke, own end zone included.
      const catchAt: Spot = depth < 0 ? [target.brk[0], lat] : at(depth, lat);
      // 5b. The throw battle (slice 10g): the passer against the target's cover man, or with
      // nobody on him the defender nearest the catch point at the throw (slot order on a tie). Its
      // margin moves the accuracy by throws.battleWeight, signed by the winner; a crit decides the
      // ball outright: the passer's is on target, the defender's a pick on a covered target and
      // incomplete on an open one. No HP changes hands (applyHp).
      const nearest = (): DefenseSlot => {
        let best = DEFENSE_SLOTS[0] as DefenseSlot;
        let bestD = Infinity;
        for (const d of DEFENSE_SLOTS) {
          const [x, y] = m.posAt(def(d), throwT);
          const dist = Math.hypot(x - catchAt[0], y - catchAt[1]);
          if (dist < bestD) [best, bestD] = [d, dist];
        }
        return best;
      };
      const reader = target.by ?? nearest();
      const tb = battle('throw', qb, reader, throwT);
      events.push({
        t: throwT,
        type: 'battle',
        a: off(qb),
        b: def(reader),
        matchup: tb,
        at: m.posAt(off(qb), throwT),
      });
      const edge = b.throws.battleWeight * (tb.margin - 1) * (tb.winner === 'a' ? 1 : -1);
      let accuracy = clamp(
        b.throws.baseAccuracy[target.route.depth] +
          (target.open ? target.steps * b.throws.openBonusPerStep : b.throws.coveredPenalty) +
          edge +
          (pressured ? b.throws.pressurePenalty : 0),
        b.throws.minAccuracy,
        b.throws.maxAccuracy,
      );
      // A crit draws no accuracy roll; the event keeps roll < accuracy exactly when on target.
      let roll: number;
      let onTarget: boolean;
      let picked = target.pick;
      if (tb.crit === 'a') {
        [accuracy, roll, onTarget] = [1, 0, true];
      } else if (tb.crit === 'b') {
        onTarget = !target.open;
        picked = onTarget;
        [accuracy, roll] = onTarget ? [1, 0] : [0, 1];
      } else {
        roll = rng.next();
        onTarget = roll < accuracy;
      }
      events.push({
        t: throwT,
        type: 'throw',
        from: off(qb),
        to: off(target.slot),
        accuracy,
        roll,
        onTarget,
        pressured,
        ...(hot ? { hot: true as const } : {}),
        ...(tb.crit ? { crit: tb.crit } : {}),
        at: onTarget ? catchAt : [Math.min(catchAt[0] + F.missYards, 100 + F.endZoneYards), lat],
        arrives: catchT,
      });
      endT = catchT;

      // 6. The catch.
      let caught = false;
      if (!onTarget) {
        events.push({
          t: catchT,
          type: 'incomplete',
          unit: off(target.slot),
          by: tb.crit === 'b' ? def(reader) : null,
        });
        result = 'incomplete';
      } else if (picked) {
        m.goto(def(target.by as DefenseSlot), catchAt, throwT, catchT);
        events.push({
          t: catchT,
          type: 'interception',
          unit: def(target.by as DefenseSlot),
          at: catchAt,
        });
        result = 'interception';
      } else if (!target.open && target.by !== undefined) {
        const r = battle('contested', target.slot, target.by, catchT);
        events.push({
          t: catchT,
          type: 'battle',
          a: off(target.slot),
          b: def(target.by),
          matchup: r,
          at: catchAt,
        });
        if (r.winner === 'a') {
          caught = true;
        } else if (r.crit === 'b' || rng.chance(b.throws.interceptionChanceWhenBrokenUp)) {
          m.goto(def(target.by), catchAt, throwT, catchT);
          events.push({ t: catchT, type: 'interception', unit: def(target.by), at: catchAt });
          result = 'interception';
        } else {
          events.push({
            t: catchT,
            type: 'incomplete',
            unit: off(target.slot),
            by: def(target.by),
          });
          result = 'incomplete';
        }
      } else {
        caught = true;
      }

      // 7. The tackle chain (slice 10l): a catch is a carry (run.ts) from the catch spot, straight
      // downfield at his speed. Everyone not held out of the play chases from where he is at the
      // throw; the target's cover man sets off at the catch from where coverage left him (his
      // separation is measured there), except on a screen, whose cover man waits upfield and
      // reads the throw. A catch at the goal line is a touchdown.
      if (caught) {
        events.push({ t: catchT, type: 'catch', unit: off(target.slot), at: catchAt });
        if (depth >= toGoal) {
          yards = toGoal;
          result = 'touchdown';
        } else {
          const track = [
            { t: catchT, at: catchAt },
            ...keysAlong(catchAt, catchT, [[100, lat]], speedOf(O[target.slot], b)),
          ];
          const react = (d: DefenseSlot) =>
            d === target.by && !target.route.carry ? catchT : throwT;
          const out = carry(ctx, target.slot, track, catchT, pursuers(react, throwT));
          ({ yards, result, endT, outOfBounds } = out);
        }
      }
    }
  }

  // Rushers who got home before the whistle, for the animation.
  if (!run && result !== 'sack' && result !== 'safety') {
    for (const a of arrivals) {
      if (a.t <= endT) events.push({ t: a.t, type: 'pressure', unit: def(a.slot) });
    }
  }

  result ??= 'incomplete';
  events.push(...m.moveEvents(endT));
  // The play is over at the whistle; anything timed later (a block that would have given, a route
  // still breaking) never happened.
  const log = applyHp(sortByTime(events.filter((e) => e.t <= endT)), next, endT, b);
  log.push({
    t: endT,
    type: 'whistle',
    yards,
    result,
    ...(outOfBounds ? { outOfBounds: true } : {}),
  });

  // 8. Downs and the spot. Turnovers and scores leave down and distance for slice 6.
  const ballOn = clamp(los + yards, 0, 100);
  next.ballOn = ballOn;
  let firstDown = false;
  let turnoverOnDowns = false;
  if (result === 'gain' || result === 'incomplete' || result === 'sack') {
    if (yards >= state.toGo) {
      firstDown = true;
      next.down = 1;
      next.toGo = Math.min(b.football.yardsToGo, 100 - ballOn);
    } else {
      next.down = state.down + 1;
      next.toGo = state.toGo - yards;
      turnoverOnDowns = next.down > b.football.downs;
    }
  }
  for (const c of [...Object.values(next.offense.lineup), ...Object.values(next.defense.lineup)]) {
    c.enteredThisPlay = false;
  }

  return { state: next, events: log, yards, result, firstDown, turnoverOnDowns, outOfBounds };
}

/** The positions hp.perSnapDrainLinemen wears down (slice 10h). */
const LINEMEN: ReadonlySet<Position> = new Set(['OL', 'DL']);

/**
 * HP from the play as it happened: after each battle the loser loses its share (hpLossForLoss)
 * and a winner that hit with an attack takes recoil or drains; a self-KO move spent at the snap
 * leaves its user at 0 at the whistle, hit or miss; every OL and DL then loses the line drain
 * (`hp.perSnapDrainLinemen`, cause 'line'). Each step clamps to [0, max HP]; a change of 0 is not
 * logged. Writes the result into `next` and returns the log with its `hp` events.
 */
function applyHp(events: PlayEvent[], next: GameState, endT: number, b: Balance): PlayEvent[] {
  const mon = (u: UnitId): Combatant => {
    const [side, slot] = u.split(':') as ['off' | 'def', string];
    return side === 'off'
      ? next.offense.lineup[slot as OffenseSlot]
      : next.defense.lineup[slot as DefenseSlot];
  };
  const out: PlayEvent[] = [];
  const change = (t: number, unit: UnitId, delta: number, cause: HpCause) => {
    const c = mon(unit);
    const hp = clamp(c.hp + delta, 0, c.stats.hp);
    if (hp === c.hp) return;
    out.push({ t, type: 'hp', unit, delta: hp - c.hp, hp, cause });
    c.hp = hp;
  };
  const { recoilScale, drainScale } = b.moves.attack;
  const selfKo: UnitId[] = [];
  for (const e of events) {
    out.push(e);
    if (e.type === 'spend') {
      const move = mon(e.unit).moves.find((m) => m.id === e.moveId);
      if (move?.effect.kind === 'attack' && move.effect.selfKo) selfKo.push(e.unit);
    }
    // The throw battle moves the ball, not HP (slice 10g), as a kick.
    if (e.type !== 'battle' || e.matchup.kind === 'throw') continue;
    const r = e.matchup;
    const [winner, loser] = r.winner === 'a' ? [e.a, e.b] : [e.b, e.a];
    const loss = hpLossForLoss(r, mon(loser).stats.hp, b);
    change(e.t, loser, -loss, 'battle');
    const use = r.winner === 'a' ? r.aAttack : r.bAttack;
    const move = use?.hit ? mon(winner).moves.find((m) => m.id === use.moveId) : undefined;
    if (!move) continue;
    const fx = attackOf(move);
    if (fx.recoil) change(e.t, winner, -Math.round(loss * fx.recoil * recoilScale), 'recoil');
    if (fx.drain) change(e.t, winner, Math.round(loss * fx.drain * drainScale), 'drain');
  }
  for (const u of selfKo) change(endT, u, -mon(u).stats.hp, 'self-ko');
  // The line wears down (slice 10h): every OL and DL loses hp.perSnapDrainLinemen a snap.
  const drain = b.hp.perSnapDrainLinemen;
  if (drain > 0) {
    const line: UnitId[] = [
      ...Object.keys(next.offense.lineup).map((s) => `off:${s}` as UnitId),
      ...Object.keys(next.defense.lineup).map((s) => `def:${s}` as UnitId),
    ].filter((u) => LINEMEN.has(SLOT_POSITION[u.split(':')[1] as OffenseSlot | DefenseSlot]));
    for (const u of line) {
      change(endT, u, -Math.round(hpBasis(mon(u).stats.hp, b) * drain), 'line');
    }
  }
  return out;
}

/** Stable sort by time, so same-time events keep resolution order. */
function sortByTime(events: PlayEvent[]): PlayEvent[] {
  return events
    .map((e, i) => [e, i] as const)
    .sort((x, y) => x[0].t - y[0].t || x[1] - y[1])
    .map(([e]) => e);
}
